import React, { useRef, useState } from 'react';
import { FormattedRoom } from '../hooks/useFormattedMonthlySetup';
import { toast } from 'sonner';
import { format, getDay, isToday } from 'date-fns';
import FullPageError from '../../../shared/components/errors/FullPageError';
import FullPageSpinner from '../../../shared/components/spinner/FullPageSpinner';
import { IconAlertTriangle, IconArrowLeft, IconArrowRight, IconCheck, IconLink, IconLock, IconLockOpen, IconSquare, IconStars } from '@tabler/icons-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatPriceShort } from '../../../shared/utils/formatPrice';
import clsx from 'clsx';
import { DynamicPricingGroupData } from '../../../services/dynamic-pricing-group/response/IndexDynamicPricingGroupResponse';
import { PriceAdjustmentData } from '../../../services/dynamic-pricing-price-ajustment/response/PriceAdjustmentResponse';
import { blendColors, getCssVariable } from '../../../shared/utils/colors';
import { useLocalStorage } from '../../../shared/hooks/useLocalStorage';
import { DynamicPricingDetailModal, renderPriceDetailPopover } from './DynamicPricingInfo';

// Peta untuk mencocokkan tipe restriksi dengan flag 'inherit' yang sesuai di API
const restrictionTypeToInheritFlagMap: { [key: string]: string } = {
    'rate': 'inherit_restriction',
    'closed_to_arrival': 'inherit_closed_to_arrival',
    'closed_to_departure': 'inherit_closed_to_departure',
    'stop_sell': 'inherit_stop_sell',
    'max_stay': 'inherit_max_stay',
    'min_stay_arrival': 'inherit_min_stay_arrival',
    'min_stay_through': 'inherit_min_stay_through',
};

/**
     * Menentukan apakah sebuah sel harus dinonaktifkan berdasarkan aturan prioritas.
     * @param rateValue - Objek rate plan yang berisi semua flag `inherit_*`.
     * @param restrictionType - Tipe restriksi dari sel yang sedang dicek (e.g., 'rate', 'stop_sell').
     * @returns {boolean} - `true` jika sel harus dinonaktifkan.
     */
const isCellDisabled = (rateValue: any, restrictionType: string): boolean => {
    // Aturan Prioritas 1: Selalu disabled jika tipe rate plan adalah 'link'
    if (rateValue.type === 'link' && restrictionType === 'rate') {
        return true;
    }

    // Aturan Prioritas 2: Periksa flag 'inherit_restriction' global. Jika 1, semua disabled.
    if (rateValue.inherit_restriction === 1) {
        return true;
    }

    // Aturan Prioritas 3: Jika global 0, periksa flag spesifik untuk tipe restriksi ini
    const specificInheritFlag = restrictionTypeToInheritFlagMap[restrictionType];
    if (rateValue[specificInheritFlag] === 1) {
        return true;
    }

    // Jika tidak ada aturan di atas yang cocok, sel aktif
    return false;
};

/**
 * Menghitung rate LINK rate plan yang diturunkan dari rate PARENT yang sudah
 * disesuaikan Dynamic Pricing (DPM).
 *
 * Baris DPM aslinya hanya terdaftar untuk PARENT (karena hanya parent yang
 * jadi item dynamic pricing group). Padahal harga LINK rate ikut berubah
 * karena diturunkan dari parent — fungsi ini MENIRU PERSIS rumus backend di
 * `RatePlanRestrictionRepository` supaya angka yang ditampilkan di FE
 * konsisten dengan yang benar-benar dikirim ke Channex. Ini murni untuk
 * kebutuhan tampilan; tidak ada perhitungan baru di sisi backend.
 *
 * @param parentRate - rate PARENT setelah disesuaikan DPM. Bisa berupa string
 *   hasil number_format dari backend (mis. "100000.00"), makanya dikonversi
 *   dengan aman di sini.
 * @param rule - 'increase' | 'decrease' dari link rate. Null berarti link
 *   ini tidak punya aturan penyesuaian.
 * @param amountType - 'percent' | 'amount' dari link rate.
 * @param value - nilai spesifik (specific_value) link rate.
 * @returns rate turunan (2 desimal), atau `null` kalau tidak bisa dihitung
 *   (parent tidak valid, atau rule/amountType tidak diset).
 */
const deriveLinkRate = (
    parentRate: number | string | null | undefined,
    rule: 'increase' | 'decrease' | null | undefined,
    amountType: 'percent' | 'amount' | null | undefined,
    value: number | null | undefined
): number | null => {
    // Konversi aman: hindari NaN kalau parentRate string kosong/undefined.
    const parent = typeof parentRate === 'string' ? parseFloat(parentRate) : parentRate;
    if (parent === null || parent === undefined || isNaN(parent)) return null;

    // Tanpa rule/amountType, tidak ada penyesuaian yang bisa diturunkan.
    if (!rule || !amountType) return null;

    const numValue = typeof value === 'number' && !isNaN(value) ? value : 0;

    let result: number;
    if (rule === 'increase' && amountType === 'percent') {
        result = parent * (1 + numValue / 100);
    } else if (rule === 'decrease' && amountType === 'percent') {
        result = parent * (1 - numValue / 100);
    } else if (rule === 'increase' && amountType === 'amount') {
        result = parent + numValue;
    } else if (rule === 'decrease' && amountType === 'amount') {
        result = parent - numValue;
    } else {
        return null;
    }

    // ROUND(x, 2) seperti di query backend.
    return Math.round(result * 100) / 100;
};

/**
 * Teks tooltip singkat untuk baris DPM milik LINK rate — supaya user tidak
 * mengira nilainya hasil rule DPM yang berlaku langsung pada link itu,
 * padahal ini murni turunan dari DPM rate induk + aturan link-nya.
 */
const describeLinkDpmAdjustment = (
    rule: 'increase' | 'decrease' | null | undefined,
    amountType: 'percent' | 'amount' | null | undefined,
    value: number | null | undefined
): string => {
    if (!rule || !amountType) {
        return 'Mengikuti DPM rate induk. Rate plan ini tidak punya aturan penyesuaian link.';
    }
    const sign = rule === 'increase' ? '+' : '-';
    const amountLabel = amountType === 'percent' ? `${value}%` : formatPriceShort(value ?? 0);
    return `Mengikuti DPM rate induk, disesuaikan ${sign}${amountLabel}`;
};

// Interface untuk objek sel yang dipilih, diekspor untuk digunakan oleh parent component.
export interface SelectedCell {
    rateId: string;
    date: string; // Format 'yyyy-MM-dd'
    rateType: string;
    restrictionType: string; // 'rate', 'closed_to_arrival', 'stop_sell', dll.
}

interface MonthlySetupTableProps {
    handlePrevMonth: () => void;
    handleThisMonth: () => void;
    handleNextMonth: () => void;
    restrictionFilter: string[];
    roomTypeFilter: string[];
    ratePlanFilter: string[];
    selectedCells: SelectedCell[];
    editedCells: SelectedCell[]; // BARU: Terima prop editedCells
    onSelectionChange: (cells: SelectedCell[]) => void;
    // BARU: Props untuk fitur Drag-to-Select
    isDragging: boolean;
    dragStartCell: SelectedCell | null;
    setIsDragging: (isDragging: boolean) => void;
    setDragStartCell: (cell: SelectedCell | null) => void;
    // Props baru untuk data dan status loading/error
    monthlySetup: FormattedRoom[];
    occupancy: any; // Sebaiknya berikan tipe yang lebih spesifik jika memungkinkan
    groupedMonths: Record<string, Date[]>;
    isFetching: boolean;
    isError: boolean;
    readyToLoad: boolean;
    dynamicPricingGroups: DynamicPricingGroupData[];
    priceAdjustments: Record<string, Record<string, PriceAdjustmentData>>;
    isReadOnly?: boolean;
}

const MonthlySetupTable: React.FC<MonthlySetupTableProps> = ({
    handlePrevMonth,
    handleThisMonth,
    handleNextMonth,
    restrictionFilter,
    roomTypeFilter,
    ratePlanFilter,
    selectedCells,
    editedCells, // BARU: Destructure prop
    onSelectionChange,
    // BARU: Destructure props untuk drag-select
    isDragging,
    dragStartCell,
    setIsDragging,
    setDragStartCell,
    // Destructuring props baru
    monthlySetup,
    occupancy,
    groupedMonths,
    isFetching,
    isError,
    readyToLoad,
    dynamicPricingGroups,
    priceAdjustments,
    isReadOnly = false,
}) => {
    // ==================================================================
    // Fungsi Helper
    // ==================================================================
    const isWeekend = (date: Date): boolean => {
        const day = getDay(date);
        return day === 0 || day === 6; // 0 = Minggu, 6 = Sabtu
    };

    const TODAY_COLOR = 'color-mix(in srgb, var(--tblr-primary) 25%, transparent)'; // Warna biru muda untuk hari ini
    const WEEKEND_COLOR = '#ffeef0';
    const LINK_COLOR = '#d3d3d3';
    const DEFAULT_COLOR = '#ffffff';
    const SELECTED_COLOR = '#e0f7fa'; // Warna biru muda untuk sel yang dipilih
    const EDITED_COLOR = '#fff9c4';   // BARU: Hijau muda untuk 'baru diubah'

    const primaryColor = getCssVariable('--tblr-primary');
    const whiteColor = '#ffffff';

    const blendedColor = blendColors(primaryColor, whiteColor, 0.25);

    const [isMobileSticky, setIsMobileSticky] = useLocalStorage('sidebarSticky', false);

    // BARU: Ref untuk membedakan klik dan drag
    const didDrag = useRef<boolean>(false);

    const isCellSelected = (cell: SelectedCell): boolean => {
        return selectedCells.some(
            (sc) =>
                sc.rateId === cell.rateId &&
                sc.date === cell.date &&
                sc.restrictionType === cell.restrictionType
        );
    };

    // BARU: Helper untuk mengecek apakah sel baru diubah
    const isCellEdited = (cell: SelectedCell): boolean => {
        return editedCells.some(
            (ec) =>
                ec.rateId === cell.rateId &&
                ec.date === cell.date &&
                ec.restrictionType === cell.restrictionType
        );
    };

    // PERBAIKAN: getCellBackgroundColor sekarang menerima `isEdited`
    const getCellBackgroundColor = (date: Date, isSelected: boolean, isEdited?: boolean, rateType?: string): string => {
        if (isSelected) return SELECTED_COLOR;
        if (isEdited) return EDITED_COLOR; // Prioritas setelah 'selected'
        if (rateType === 'link') return LINK_COLOR;
        if (isToday(date)) return TODAY_COLOR;
        if (isWeekend(date)) return WEEKEND_COLOR;
        return DEFAULT_COLOR;
    };

    // Sel DPM yang sedang dibuka detailnya (klik sel → modal penjelasan lengkap).
    const [dpmDetail, setDpmDetail] = useState<{ data: PriceAdjustmentData; ratePlanName?: string } | null>(null);

    const hasDynamicPricingItem = (roomTypeId: string, ratePlanId: string): boolean => {
        return dynamicPricingGroups.some(group =>
            group.is_active && group.items?.some(item =>
                item.room_type_id === roomTypeId && item.rate_plan_id === ratePlanId
            )
        );
    };

    const getHeaderTextColor = (date: Date): string => {
        if (isToday(date)) return '#000000';
        if (isWeekend(date)) return '#c04c60';
        return '#0f6e77';
    }

    // ==================================================================
    // Logika Klik Sel dengan Validasi Tipe
    // ==================================================================
    const handleCellClick = (cell: SelectedCell, isDisabled: boolean) => {
        if (isReadOnly || isDisabled) return;

        const alreadySelected = isCellSelected(cell);

        if (alreadySelected) {
            // Logika untuk membatalkan pilihan (deselect)
            // Tidak perlu validasi, cukup hapus sel dari array.
            const newSelection = selectedCells.filter(
                (sc) =>
                    !(sc.rateId === cell.rateId && sc.date === cell.date && sc.restrictionType === cell.restrictionType)
            );
            onSelectionChange(newSelection);
        } else {
            // Logika untuk memilih sel baru (select)

            // --- VALIDASI TIPE SELEKSI ---
            if (selectedCells.length > 0) {
                // Ambil tipe restriksi dari sel pertama yang sudah ada.
                const currentSelectionType = selectedCells[0].restrictionType;

                // Bandingkan dengan tipe sel yang baru diklik.
                if (cell.restrictionType !== currentSelectionType) {
                    // Jika tipe berbeda, tampilkan notifikasi dan hentikan proses.
                    toast.warning('You can only select cells of the same restriction type.', {
                        id: 'selection-type-warning' // ID mencegah toast duplikat jika diklik cepat
                    });
                    return; // Menghentikan penambahan sel baru
                }
            }
            // --- AKHIR VALIDASI ---

            // Jika validasi lolos, tambahkan sel baru ke dalam array.
            const newSelection = [...selectedCells, cell];
            onSelectionChange(newSelection);
        }
    };

    // ====================================================================
    // Event Handlers untuk Drag-to-Select (Logika Diperbarui)
    // ====================================================================

    const handleMouseDown = (cell: SelectedCell, isDisabled: boolean) => {
        if (isReadOnly || isDisabled) return;
        setIsDragging(true);
        setDragStartCell(cell);
        didDrag.current = false; // Reset bendera drag setiap kali mouse ditekan
    };

    const handleMouseEnter = (hoverCell: SelectedCell, isDisabled: boolean) => {
        if (!isDragging || !dragStartCell || isDisabled) return;

        // Set bendera bahwa drag telah terjadi
        didDrag.current = true;

        if (hoverCell.restrictionType !== dragStartCell.restrictionType || hoverCell.rateId !== dragStartCell.rateId) return;

        let availableDates: string[] = [];
        for (const room of monthlySetup) {
            const rate = room.rates.find(r => r.id === dragStartCell.rateId);
            if (rate) {
                availableDates = rate.restictions.map((r: any) => format(new Date(r[0]), 'yyyy-MM-dd'));
                break;
            }
        }

        if (availableDates.length === 0) return;

        const startIndex = availableDates.indexOf(dragStartCell.date);
        const hoverIndex = availableDates.indexOf(hoverCell.date);

        if (startIndex === -1 || hoverIndex === -1) return;

        const start = Math.min(startIndex, hoverIndex);
        const end = Math.max(startIndex, hoverIndex);

        const datesToSelect = availableDates.slice(start, end + 1);

        const newSelectedCells = datesToSelect.map(dateStr => ({
            ...dragStartCell,
            date: dateStr,
        }));

        onSelectionChange(newSelectedCells);
    };

    const handleMouseUp = (cell: SelectedCell, isDisabled: boolean) => {
        // Jika tidak ada drag yang terjadi, maka ini adalah klik biasa
        if (!didDrag.current) {
            handleCellClick(cell, isDisabled);
        }
        // Proses drag selesai, reset state (listener global juga akan membantu)
        setIsDragging(false);
        setDragStartCell(null);
    };

    // ==================================================================
    // Fetching Data dan Render Komponen (Logika dipindah ke parent)
    // ==================================================================
    if (isError) {
        toast.error('Failed to fetch data', { id: 'monthly-setup-fetch-error' });
    }

    if (!isFetching && monthlySetup.length === 0) {
        const message = `We encountered an issue with your room and rate setup.\nKindly review your configuration and make sure everything is set correctly.`;
        return <FullPageError message={message} />;
    }

    return (
        <>
            {isError && <FullPageError />}
            {isFetching && <FullPageSpinner />}
            {readyToLoad && (
                <>
                    {/* 3. Tambahkan Tombol Toggle (HANYA MUNCUL DI MOBILE) */}
                    <div className="d-lg-none p-2" style={{ backgroundColor: '#f8f9fa' }}>
                        {/* 'd-lg-none' adalah kelas dari Bootstrap untuk 'display: none' di layar large ke atas */}
                        <button
                            className="btn btn-sm btn-outline-secondary w-100"
                            onClick={() => setIsMobileSticky(prev => !prev)}
                        >
                            {isMobileSticky ? (
                                <><IconLockOpen size={16} className="me-2" /> Unpin Sidebar (Scroll)</>
                            ) : (
                                <><IconLock size={16} className="me-2" /> Pin Sidebar (Sticky)</>
                            )}
                        </button>
                    </div>

                    <div className={clsx(
                        "table border-top max-vw-100 overflow-hidden overflow-x-auto",
                        isMobileSticky && "mobile-sticky-active" // INI BAGIAN PENTINGNYA
                    )}>
                        <table className="table table-bordered table-hover table-vcenter">
                            <thead>
                                <tr>
                                    <th rowSpan={2} style={{ maxWidth: '300px' }} className='text-center monthly-setup-sidebar'>
                                        <h2 className='fw-bold fs-2 text-capitalize text-secondary'>Rates & Availabilities</h2>
                                        <div className='px-6'>
                                            <div className='d-flex align-items-center justify-content-center'>
                                                <button className='btn btn-sm btn-outline-primary btn-pill me-1' onClick={handlePrevMonth}>
                                                    <IconArrowLeft size={12} />
                                                </button>
                                                <button className='btn btn-sm btn-outline-primary w-100 btn-pill me-1' onClick={handleThisMonth}>
                                                    THIS MONTH
                                                </button>
                                                <button className='btn btn-sm btn-outline-primary btn-pill me-1' onClick={handleNextMonth}>
                                                    <IconArrowRight size={12} />
                                                </button>
                                            </div>
                                        </div>
                                    </th>
                                    {Object.keys(groupedMonths).map((monthYear, index) => (
                                        <th key={index} colSpan={groupedMonths[monthYear].length} style={{ height: '3.5rem' }} className='text-primary fs-4 text-capitalize'>
                                            {monthYear}
                                        </th>
                                    ))}
                                </tr>
                                <tr>
                                    {occupancy.data.map((dayData: any) => {
                                        const currentDate = new Date(dayData.date);
                                        return (
                                            <th key={dayData.date} className='text-center fw-bold text-capitalize' style={{
                                                backgroundColor: getCellBackgroundColor(currentDate, false),
                                                color: getHeaderTextColor(currentDate)
                                            }}>
                                                {format(currentDate, 'd EEE')}
                                                <br /> {dayData.occupancy}%
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {monthlySetup.map((room: FormattedRoom) => (
                                    <React.Fragment key={room.id}>
                                        {roomTypeFilter.includes(room.id) && (
                                            <>
                                                {/* Baris Availability (tidak bisa dipilih) */}
                                                <tr>
                                                    <td className='py-1 cursor-pointer monthly-setup-sidebar' style={{ backgroundColor: blendedColor || 'color-mix(in srgb, var(--tblr-primary) 25%, transparent)' }}>
                                                        <div className='d-flex align-items-center justify-content-between'>
                                                            <h3 className='fw-semi-bold mb-0 text-primary fs-4' style={{ fontWeight: 500 }}>{room.name}</h3>
                                                            <span className='fw-bold fs-5'>AVL</span>
                                                        </div>
                                                    </td>
                                                    {room.availability.map((avl: any, key: number) => {
                                                        const backgroundColor = avl[1] === 0
                                                            ? '#aaaaaa' // Warna abu-abu/secondary jika avl adalah 0
                                                            : getCellBackgroundColor(new Date(avl[0]), false);

                                                        return (
                                                            // <td className='text-center py-1 cursor-not-allowed fs-5' key={`${room.id}-${avl[0]}-${key}`} style={{ backgroundColor: getCellBackgroundColor(new Date(avl[0]), false) }}>
                                                            <td className='text-center py-1 cursor-not-allowed fs-5' key={`${room.id}-${avl[0]}-${key}`} style={{ backgroundColor: backgroundColor }}>
                                                                {avl[1]}
                                                            </td>
                                                        )
                                                    })}
                                                </tr>
                                                {/* Baris Rate Plan dan Restriksi */}
                                                {room.rates.map((rateValue: any, rateKey: number) => {
                                                    const localStyles = { ...(rateKey === (room.rates.length - 1) && { borderBottom: '1px solid black' }) };

                                                    const isDynamicPricingEnabled = hasDynamicPricingItem(room.id, rateValue.id);

                                                    // BARU: baris DPM juga muncul untuk LINK rate, tapi syaratnya
                                                    // PARENT-nya yang terdaftar sebagai item dynamic pricing group
                                                    // aktif (link sendiri tidak pernah jadi item DPM).
                                                    const isParentDynamicPricingEnabled = rateValue.type === 'link' && rateValue.parent_id
                                                        ? hasDynamicPricingItem(room.id, rateValue.parent_id)
                                                        : false;

                                                    return (
                                                        <React.Fragment key={`${rateValue.id}-${rateKey}`}>
                                                            {ratePlanFilter.includes(rateValue.id) && (
                                                                <>
                                                                    {/* Baris Rate */}
                                                                    {restrictionFilter.includes('rate') && (
                                                                        <tr>
                                                                            <td className='py-1 monthly-setup-sidebar' style={{ backgroundColor: rateValue.type === 'link' ? LINK_COLOR : DEFAULT_COLOR }}>
                                                                                <div className='d-flex align-items-center justify-content-between'>
                                                                                    <h3 className='fw-semi-bold mb-0 fs-4 text-primary ms-2' style={{ fontWeight: 500 }}>{rateValue.name}</h3>
                                                                                    <span className='fw-bold fs-5 d-flex align-items-center'>
                                                                                        RATE
                                                                                        {rateValue.type === 'link' && (
                                                                                            <OverlayTrigger placement="right" overlay={<Tooltip id={`tooltip-link-${rateValue.id}`}>Rates derived from <span style={{ textDecoration: 'underline' }}>{room.rates.find((r: any) => r.id === rateValue.parent_id)?.name}</span></Tooltip>}>
                                                                                                <IconLink size={14} className='ms-2' />
                                                                                            </OverlayTrigger>
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                            </td>
                                                                            {rateValue.restictions.map((restriction: any, restrictionKey: number) => {
                                                                                const currentDate = new Date(restriction[0]);
                                                                                const cellData: SelectedCell = { rateId: rateValue.id, date: format(currentDate, 'yyyy-MM-dd'), rateType: rateValue.type, restrictionType: 'rate' };
                                                                                // const isDisabled = rateValue.inherit_restriction || rateValue.type === 'link';
                                                                                const isDisabled = isCellDisabled(rateValue, 'rate');
                                                                                const isSelected = isCellSelected(cellData);
                                                                                const isEdited = isCellEdited(cellData); // BARU: Cek status 'edited'
                                                                                const isOutAvailablity = room.availability.find((avl: any) => avl[0] === restriction[0])?.[1] === 0;

                                                                                return (
                                                                                    <td
                                                                                        className={clsx('text-center py-1 fs-5', isDisabled ? 'cursor-not-allowed' : 'cursor-pointer', isOutAvailablity && 'text-disabled')}
                                                                                        key={`${restrictionKey}-rate`}
                                                                                        style={{ backgroundColor: getCellBackgroundColor(currentDate, isSelected, isEdited, rateValue.type) }}
                                                                                        // onClick={() => handleCellClick(cellData, isDisabled)}
                                                                                        onMouseDown={() => handleMouseDown(cellData, isDisabled)}
                                                                                        onMouseEnter={() => handleMouseEnter(cellData, isDisabled)}
                                                                                        onMouseUp={() => handleMouseUp(cellData, isDisabled)} // Ganti onClick dengan onMouseUp
                                                                                    >
                                                                                        {formatPriceShort(restriction[1].rate)}
                                                                                    </td>
                                                                                );
                                                                            })}
                                                                        </tr>
                                                                    )}

                                                                    {isDynamicPricingEnabled && (
                                                                        <tr>
                                                                            <td className='py-1 monthly-setup-sidebar bg-white'>
                                                                                <div className='d-flex align-items-center justify-content-end'>
                                                                                    <span className='fw-bold fs-5 me-2'>DPM</span>
                                                                                    <OverlayTrigger placement="right" overlay={<Tooltip id="tooltip-dynamic-pricing">Rates adjusted by Dynamic Pricing Manager. Hover a cell to see why, or click it for the full breakdown.</Tooltip>}>
                                                                                        <IconStars size={14} className='text-warning' />
                                                                                    </OverlayTrigger>
                                                                                </div>
                                                                            </td>
                                                                            {room.rates.find(r => r.id === rateValue.id)?.restictions.map((restriction: any, restrictionKey: number) => {
                                                                                const dateStr = format(new Date(restriction[0]), 'yyyy-MM-dd');
                                                                                const dpData = priceAdjustments[rateValue.id]?.[dateStr];

                                                                                // Tanpa data sama sekali tidak ada yang bisa dijelaskan.
                                                                                // (Sebelumnya baris ini mengakses dpData.is_dynamic_pricing
                                                                                // tanpa guard sehingga crash saat datanya kosong.)
                                                                                if (!dpData) {
                                                                                    return (
                                                                                        <td key={`${restrictionKey}-dp`} className='text-center py-1 fs-5 text-black'>-</td>
                                                                                    );
                                                                                }

                                                                                // Backend tetap menjepit rate dengan min/max override WALAU
                                                                                // tidak ada rule yang cocok — jadi angka di sel bisa berbeda
                                                                                // dari rate induk meski is_dynamic_pricing = false. Karena itu
                                                                                // popover dipasang untuk SEMUA sel yang punya data, bukan cuma
                                                                                // yang is_dynamic_pricing (perilaku lama membuat sel "kena
                                                                                // limit" tidak punya penjelasan sama sekali).
                                                                                const isCapped = Boolean(
                                                                                    dpData.dynamic_pricing_info?.min_rate_override_applied ||
                                                                                    dpData.dynamic_pricing_info?.max_rate_override_applied
                                                                                );

                                                                                return (
                                                                                    <OverlayTrigger
                                                                                        key={`${restrictionKey}-dp`}
                                                                                        trigger={['hover', 'focus']}
                                                                                        placement="top"
                                                                                        overlay={renderPriceDetailPopover(dpData)}
                                                                                    >
                                                                                        <td
                                                                                            className={clsx(
                                                                                                'text-center py-1 fs-5 cursor-help',
                                                                                                isCapped ? 'text-warning fw-bold' : dpData.is_dynamic_pricing ? 'text-danger' : 'text-black'
                                                                                            )}
                                                                                            onClick={() => setDpmDetail({ data: dpData, ratePlanName: rateValue.name })}
                                                                                        >
                                                                                            {dpData.rate ? formatPriceShort(dpData.rate) : '-'}
                                                                                            {isCapped && <IconAlertTriangle size={11} className='ms-1 align-text-top' />}
                                                                                        </td>
                                                                                    </OverlayTrigger>
                                                                                );
                                                                            })}
                                                                        </tr>
                                                                    )}

                                                                    {/* BARU: Baris DPM untuk LINK rate — nilai TURUNAN dari DPM
                                                                        rate induk, bukan hasil rule DPM yang berlaku langsung pada
                                                                        link ini. Karena itu popover/modal penjelasan milik parent
                                                                        SENGAJA tidak dipakai ulang di sini, cukup tooltip sederhana. */}
                                                                    {isParentDynamicPricingEnabled && (
                                                                        <tr>
                                                                            <td className='py-1 monthly-setup-sidebar' style={{ backgroundColor: LINK_COLOR }}>
                                                                                <div className='d-flex align-items-center justify-content-end'>
                                                                                    <OverlayTrigger placement="right" overlay={<Tooltip id={`tooltip-link-dpm-${rateValue.id}`}>{describeLinkDpmAdjustment(rateValue.specific_rule, rateValue.specific_amount_type, rateValue.specific_value)}</Tooltip>}>
                                                                                        <span className='fw-bold fs-5 me-2 d-flex align-items-center'>
                                                                                            DPM <IconLink size={12} className='ms-1' />
                                                                                        </span>
                                                                                    </OverlayTrigger>
                                                                                </div>
                                                                            </td>
                                                                            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- konsisten dengan pola `restriction: any` yang sudah dipakai di baris RATE/DPM/restriksi lain pada file ini */}
                                                                            {rateValue.restictions.map((restriction: any, restrictionKey: number) => {
                                                                                const dateStr = format(new Date(restriction[0]), 'yyyy-MM-dd');
                                                                                // Ambil penyesuaian DPM milik PARENT, lalu turunkan ke rate
                                                                                // link ini dengan rumus yang sama persis dengan backend.
                                                                                const parentAdjustment = priceAdjustments[rateValue.parent_id]?.[dateStr];

                                                                                if (!parentAdjustment) {
                                                                                    return (
                                                                                        <td key={`${restrictionKey}-dp-link`} className='text-center py-1 fs-5 text-black'>-</td>
                                                                                    );
                                                                                }

                                                                                const derivedRate = deriveLinkRate(
                                                                                    parentAdjustment.rate,
                                                                                    rateValue.specific_rule,
                                                                                    rateValue.specific_amount_type,
                                                                                    rateValue.specific_value
                                                                                );

                                                                                return (
                                                                                    <OverlayTrigger
                                                                                        key={`${restrictionKey}-dp-link`}
                                                                                        trigger={['hover', 'focus']}
                                                                                        placement="top"
                                                                                        overlay={<Tooltip id={`tooltip-link-dpm-cell-${rateValue.id}-${restrictionKey}`}>{describeLinkDpmAdjustment(rateValue.specific_rule, rateValue.specific_amount_type, rateValue.specific_value)}</Tooltip>}
                                                                                    >
                                                                                        <td className='text-center py-1 fs-5 cursor-help text-black' style={{ backgroundColor: LINK_COLOR }}>
                                                                                            {derivedRate !== null ? formatPriceShort(derivedRate) : '-'}
                                                                                        </td>
                                                                                    </OverlayTrigger>
                                                                                );
                                                                            })}
                                                                        </tr>
                                                                    )}

                                                                    {/* Looping untuk baris restriksi lainnya */}
                                                                    {['cta', 'ctd', 'ss', 'mxs', 'msa', 'mst'].map((type) => {
                                                                        if (!restrictionFilter.includes(type)) return null;
                                                                        const configMap = { cta: { label: 'CTA', inherit: 'inherit_closed_to_arrival', data: 'closed_to_arrival', bool: true, type: 'closed_to_arrival' }, ctd: { label: 'CTD', inherit: 'inherit_closed_to_departure', data: 'closed_to_departure', bool: true, type: 'closed_to_departure' }, ss: { label: 'STOP', inherit: 'inherit_stop_sell', data: 'stop_sell', bool: true, type: 'stop_sell' }, mxs: { label: 'MAX', inherit: 'inherit_max_stay', data: 'max_stay', bool: false, type: 'max_stay' }, msa: { label: 'MIN', inherit: 'inherit_min_stay_arrival', data: 'min_stay_arrival', bool: false, type: 'min_stay_arrival' }, mst: { label: 'MIN-T', inherit: 'inherit_min_stay_through', data: 'min_stay_through', bool: false, type: 'min_stay_through' } };
                                                                        const config = configMap[type as keyof typeof configMap];
                                                                        return (
                                                                            <tr key={type} style={type === 'mst' ? localStyles : {}}>
                                                                                <td className='py-1 monthly-setup-sidebar bg-white'>
                                                                                    <div className='d-flex align-items-center justify-content-end'><span className='fw-bold fs-5'>{config.label}</span></div>
                                                                                </td>
                                                                                {rateValue.restictions.map((restriction: any, restrictionKey: number) => {
                                                                                    const currentDate = new Date(restriction[0]);
                                                                                    const cellData: SelectedCell = { rateId: rateValue.id, date: format(currentDate, 'yyyy-MM-dd'), rateType: rateValue.type, restrictionType: config.type };
                                                                                    // const isDisabled = rateValue[config.inherit] || rateValue.type === 'link';
                                                                                    const isDisabled = isCellDisabled(rateValue, config.type);
                                                                                    const isSelected = isCellSelected(cellData);
                                                                                    const isEdited = isCellEdited(cellData); // BARU: Cek status 'edited'
                                                                                    const dataValue = restriction[1][config.data];
                                                                                    return (
                                                                                        <td
                                                                                            key={`${restrictionKey}-${type}`}
                                                                                            className={clsx('text-center text-black p-1 fs-5', isDisabled ? 'cursor-not-allowed' : 'cursor-pointer')}
                                                                                            style={{ backgroundColor: getCellBackgroundColor(currentDate, isSelected, isEdited) }}
                                                                                            // onClick={() => handleCellClick(cellData, isDisabled)}
                                                                                            onMouseDown={() => handleMouseDown(cellData, isDisabled)}
                                                                                            onMouseEnter={() => handleMouseEnter(cellData, isDisabled)}
                                                                                            onMouseUp={() => handleMouseUp(cellData, isDisabled)} // Ganti onClick dengan onMouseUp
                                                                                        >
                                                                                            {config.bool ? (dataValue ? <IconCheck size={14} className='text-primary' /> : <IconSquare size={14} className='text-primary' />) : (dataValue && <span>{dataValue}</span>)}
                                                                                        </td>
                                                                                    );
                                                                                })}
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <DynamicPricingDetailModal
                show={!!dpmDetail}
                onClose={() => setDpmDetail(null)}
                data={dpmDetail?.data ?? null}
                ratePlanName={dpmDetail?.ratePlanName}
            />
        </>
    );
};

export default MonthlySetupTable;